/**
 * Utilidades y HTML de impresión empresarial (carta 8.5x11)
 * compartido por VistaCotizacion, VistaFactura, VistaCompra y VistaNotaCredito.
 */

export const money = (n) => `$${Number(n || 0).toFixed(2)}`;

/** Extrae año/mes/día de una fecha ISO sin interpretar UTC (evita el día menos). */
export const partesFechaLocal = (f) => {
  const raw = String(f || '').trim();
  if (!raw) return null;
  const iso = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return { y: iso[1], m: iso[2], d: iso[3] };
  const dmy = raw.match(/^(\d{2})[/-](\d{2})[/-](\d{4})/);
  if (dmy) return { d: dmy[1], m: dmy[2], y: dmy[3] };
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return null;
  return {
    y: String(d.getFullYear()),
    m: String(d.getMonth() + 1).padStart(2, '0'),
    d: String(d.getDate()).padStart(2, '0'),
  };
};

export const fechaEmisionDgii = (f) => {
  const p = partesFechaLocal(f);
  return p ? `${p.d}-${p.m}-${p.y}` : String(f || '');
};

const CAMPOS_FECHA_VENCIMIENTO = [
  'fechavencimiento',
  'fechavencimientosecuencia',
  'fecha_valida',
  'fechaValida',
  'valido',
];

const textoFechaVencimiento = (valor) => {
  const raw = String(valor ?? '').trim();
  if (!raw || raw === 'None' || raw === 'null' || raw === 'undefined') return '';
  return fechaEmisionDgii(raw);
};

/** Toma la fecha de vencimiento del NCF desde cualquiera de los alias usados
 *  en facturas, notas y e-CF (emisión / reimpresión). */
export const resolverFechaVencimiento = (...fuentes) => {
  for (const fuente of fuentes) {
    if (fuente == null || fuente === '') continue;
    if (typeof fuente !== 'object') {
      const formateada = textoFechaVencimiento(fuente);
      if (formateada) return formateada;
      continue;
    }
    for (const key of CAMPOS_FECHA_VENCIMIENTO) {
      const formateada = textoFechaVencimiento(fuente[key]);
      if (formateada) return formateada;
    }
  }
  return '';
};

export const formatoFechaLargo = (f) => {
  if (!f) return '—';
  const p = partesFechaLocal(f);
  if (!p) return f;
  return new Date(Number(p.y), Number(p.m) - 1, Number(p.d)).toLocaleDateString('es-DO', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

export const formatoFechaCorto = (f) => {
  if (!f) return '—';
  const p = partesFechaLocal(f);
  return p ? `${p.d}/${p.m}/${p.y}` : f;
};

export const escHtml = (value) =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

/**
 * CSS carta 8.5x11: el QR viaja junto a los totales y no fuerza una hoja extra
 * cuando todavía cabe en la página actual.
 */
export const cssQrCarta = `
    .cierre-doc {
      width: 100%;
      border-collapse: collapse;
      margin-top: 14px;
      page-break-inside: avoid;
      break-inside: avoid;
      page-break-before: avoid;
      break-before: avoid;
    }
    .cierre-doc td {
      vertical-align: bottom;
      padding: 0;
      border: none;
    }
    .cierre-doc .cierre-qr {
      width: 1%;
      white-space: nowrap;
      padding-right: 16px;
      vertical-align: bottom;
    }
    .qr-ecf {
      page-break-inside: avoid;
      break-inside: avoid;
      page-break-before: avoid;
      break-before: avoid;
      page-break-after: avoid;
      break-after: avoid;
    }
    .qr-ecf img {
      width: 92px;
      height: 92px;
      display: block;
    }
    .qr-ecf .qr-meta {
      font-size: 10px;
      color: #4b5563;
      margin-top: 3px;
      line-height: 1.3;
    }
`;

export const htmlBloqueQrCarta = ({
  qrDataUrl = '',
  codigoSeguridad = '',
  fechaFirma = '',
  mostrarComprobanteElectronico,
} = {}) => {
  if (!qrDataUrl) return '';
  if (mostrarComprobanteElectronico === false) return '';
  return `<div class="qr-ecf">
    <img src="${qrDataUrl}" alt="QR DGII" width="92" height="92" />
    ${codigoSeguridad ? `<div class="qr-meta">Código Seg: ${escHtml(codigoSeguridad)}</div>` : ''}
    ${fechaFirma ? `<div class="qr-meta">Fecha Firma: ${escHtml(fechaFirma)}</div>` : ''}
  </div>`;
};

export const envolverCierreCarta = (totalesHtml, qrOpts = {}) => {
  const qr = htmlBloqueQrCarta(qrOpts);
  if (!qr) return totalesHtml;
  return `<table class="cierre-doc"><tr><td class="cierre-qr">${qr}</td><td>${totalesHtml}</td></tr></table>`;
};

/**
 * Si el QR todavía cabe en la última página de contenido, lo compacta para
 * que no quede solo en una hoja extra (Chrome a menudo empuja imágenes).
 */
export const scriptQrCartaSinSalto = `
<script>
(function () {
  function altoPaginaPx() {
    return (11 * 96) - (32 * 96 / 25.4);
  }
  function ajustarQrCarta() {
    var qr = document.querySelector('.qr-ecf');
    if (!qr) return;
    var img = qr.querySelector('img');
    var pageH = altoPaginaPx();
    var size = img ? (parseInt(img.getAttribute('width'), 10) || img.offsetWidth || 92) : 92;
    var i = 0;
    while (i < 10) {
      var h = document.body.scrollHeight;
      if (h <= pageH + 8) return;
      var lastUsed = h - Math.floor((h - 1) / pageH) * pageH;
      if (lastUsed > qr.offsetHeight + 72) return;
      size = Math.max(52, size - 8);
      if (img) {
        img.style.width = size + 'px';
        img.style.height = size + 'px';
      }
      qr.style.margin = '0';
      i += 1;
      if (size <= 52) return;
    }
  }
  function cuandoImagenLista(fn) {
    var img = document.querySelector('.qr-ecf img');
    if (!img || img.complete) { fn(); return; }
    img.addEventListener('load', fn);
    img.addEventListener('error', fn);
  }
  cuandoImagenLista(ajustarQrCarta);
})();
</script>
`;

/**
 * Genera HTML carta empresarial para impresión.
 *
 * @param {Object} opts
 * @param {string} opts.tituloDocumento - Ej. FACTURA, COMPRA, NOTA DE CRÉDITO
 * @param {string} opts.numero
 * @param {string} [opts.documento]
 * @param {string} opts.fecha
 * @param {Array<{label:string,value:string}>} [opts.metaExtra]
 * @param {{titulo?:string,nombre?:string,lineas?:string[]}} [opts.parte]
 * @param {{titulo?:string,lineas?:string[]}} [opts.bloqueExtra]
 * @param {Array} opts.items
 * @param {Array<{label:string,value:string|number,danger?:boolean}>} opts.filasTotales
 * @param {string} opts.totalLabel
 * @param {number|string} opts.totalValue
 * @param {string} [opts.observaciones]
 * @param {string} [opts.qrDataUrl]
 * @param {string} [opts.footerNote]
 * @param {string} [opts.mensajeGracias]
 * @param {string} [opts.leyendaPiePagina]
 */
export const generarHtmlDocumentoEmpresarial = ({
  tituloDocumento = 'DOCUMENTO',
  numero = '',
  documento = '',
  fecha = '',
  fechaVencimiento = '',
  metaExtra = [],
  parte = null,
  bloqueExtra = null,
  items = [],
  filasTotales = [],
  totalLabel = 'TOTAL',
  totalValue = 0,
  observaciones = '',
  qrDataUrl = '',
  codigoSeguridad = '',
  fechaFirma = '',
  footerNote = '',
  mensajeGracias = '',
  leyendaPiePagina = '',
  nombreEmpresa = 'Empresa',
  direccionEmpresa = '',
  telefonoEmpresa = '',
  rncEmpresa = '',
  emailEmpresa = '',
  mostrarInfoEmpresa = true,
  ncf_factura_origen = '',
}) => {
  //const numeroMostrar = documento  ? `${documento}-${numero}` : numero || documento || '—';
  const numeroMostrar = documento  ? `${documento}` : 'Sin documento' || documento || '—';

  const itemsHtml =
    !items || items.length === 0
      ? `<tr><td colspan="6" style="text-align:center;padding:22px;color:#888;">Sin ítems</td></tr>`
      : items
          .map(
            (item, idx) => `
        <tr style="background:${idx % 2 === 0 ? '#ffffff' : '#f7f8fa'};">
          <td style="padding:10px 8px;border-bottom:1px solid #e6e8ec;font-size:12px;color:#555;">${escHtml(item.codigo || '—')}</td>
          <td style="padding:10px 8px;border-bottom:1px solid #e6e8ec;font-size:12px;color:#222;">${escHtml(item.descripcion || '—')}${
            item.subdescripcion
              ? `<div style="font-size:11px;color:#666;margin-top:2px;">${escHtml(item.subdescripcion)}</div>`
              : ''
          }</td>
          <td style="padding:10px 8px;border-bottom:1px solid #e6e8ec;text-align:center;font-size:12px;">${parseFloat(item.cantidad || 0)}</td>
          <td style="padding:10px 8px;border-bottom:1px solid #e6e8ec;text-align:right;font-size:12px;">${money(item.precio)}</td>
          <td style="padding:10px 8px;border-bottom:1px solid #e6e8ec;text-align:right;font-size:12px;">${money(item.descuento)}</td>
          <td style="padding:10px 8px;border-bottom:1px solid #e6e8ec;text-align:right;font-size:12px;font-weight:700;">${money(item.subtotal)}</td>
        </tr>`
          )
          .join('');

  const metaExtraHtml = (metaExtra || [])
    .filter((m) => m && m.value)
    .map(
      (m) =>
        `<div style="font-size:12px;color:#4b5563;margin:2px 0;">${escHtml(m.label)}${m.label ? ': ' : ''}${escHtml(m.value)}</div>`
    )
    .join('');

  const filasTotalesHtml = (filasTotales || [])
    .map(
      (f) => `
      <div class="row">
        <span>${escHtml(f.label)}</span>
        <span style="${f.danger ? 'color:#b91c1c;' : ''}">${escHtml(typeof f.value === 'number' ? money(f.value) : f.value)}</span>
      </div>`
    )
    .join('');

  const parteHtml =
    parte && (parte.nombre || (parte.lineas && parte.lineas.length))
      ? `
    <div style="background:#f7f8fa;border:1px solid #e5e7eb;border-radius:6px;padding:14px 16px;margin-bottom:22px;">
      <div class="section-title" style="border:none;padding:0;margin-bottom:10px;">${escHtml(parte.titulo || 'Cliente')}</div>
      <div style="font-weight:700;color:#111;font-size:14px;margin-bottom:4px;">${escHtml(parte.nombre || '—')}</div>
      ${(parte.lineas || [])
        .filter(Boolean)
        .map((l) => `<div style="font-size:12px;color:#374151;margin:2px 0;">${escHtml(l)}</div>`)
        .join('')}
      ${
        bloqueExtra && bloqueExtra.lineas && bloqueExtra.lineas.length
          ? `<div style="margin-top:12px;padding-top:10px;border-top:1px solid #e5e7eb;">
              <div style="font-size:10px;letter-spacing:0.08em;text-transform:uppercase;color:#6b7280;font-weight:700;margin-bottom:4px;">${escHtml(bloqueExtra.titulo || '')}</div>
              ${bloqueExtra.lineas
                .filter(Boolean)
                .map((l) => `<div style="font-size:12px;color:#374151;margin:2px 0;">${escHtml(l)}</div>`)
                .join('')}
            </div>`
          : ''
      }
    </div>`
      : bloqueExtra && bloqueExtra.lineas && bloqueExtra.lineas.length
        ? `
    <div style="background:#f7f8fa;border:1px solid #e5e7eb;border-radius:6px;padding:14px 16px;margin-bottom:22px;">
      <div class="section-title" style="border:none;padding:0;margin-bottom:10px;">${escHtml(bloqueExtra.titulo || '')}</div>
      ${bloqueExtra.lineas
        .filter(Boolean)
        .map((l) => `<div style="font-size:12px;color:#374151;margin:2px 0;">${escHtml(l)}</div>`)
        .join('')}
    </div>`
        : '';
  
  const totalesHtml = `<div class="totales">
    ${filasTotalesHtml}
    <div class="total"><span>${escHtml(totalLabel)}</span><span>${escHtml(typeof totalValue === 'number' ? money(totalValue) : totalValue)}</span></div>
  </div>`;
  const cierreHtml = envolverCierreCarta(totalesHtml, { qrDataUrl, codigoSeguridad, fechaFirma });

  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <title>${escHtml(tituloDocumento)} ${escHtml(numeroMostrar)}</title>
  <style>
    @page { size: letter; margin: 16mm; }
    @media print {
      body { margin: 0 !important; padding: 0 !important; }
      * { box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    }
    body {
      font-family: "Segoe UI", "Helvetica Neue", Arial, sans-serif;
      color: #1a1f2c;
      font-size: 13px;
      line-height: 1.5;
      margin: 0;
      padding: 16mm;
      background: #fff;
    }
    .accent { height: 4px; background: #0E1F33; margin: 0 0 22px 0; }
    .meta-label {
      font-size: 10px;
      letter-spacing: 0.12em;
      text-transform: uppercase;
      color: #6b7280;
      font-weight: 700;
      margin-bottom: 4px;
    }
    .section-title {
      font-size: 11px;
      letter-spacing: 0.1em;
      text-transform: uppercase;
      color: #0E1F33;
      font-weight: 700;
      margin: 0 0 8px 0;
      padding-bottom: 6px;
      border-bottom: 1px solid #e5e7eb;
    }
    table.items { width: 100%; border-collapse: collapse; margin: 8px 0 0 0; }
    table.items th {
      background: #0E1F33;
      color: #fff;
      padding: 11px 8px;
      text-align: left;
      font-size: 10px;
      letter-spacing: 0.08em;
      text-transform: uppercase;
      font-weight: 700;
    }
    ${cssQrCarta}
    .totales {
      width: 280px;
      margin-left: auto;
      margin-top: 22px;
      border: 1px solid #e5e7eb;
      border-radius: 6px;
      overflow: hidden;
    }
    .cierre-doc .totales { margin-top: 0; }
    .totales .row {
      display: flex;
      justify-content: space-between;
      padding: 8px 14px;
      font-size: 12px;
      color: #4b5563;
      border-bottom: 1px solid #f0f1f3;
    }
    .totales .total {
      background: #0E1F33;
      color: #fff;
      font-size: 16px;
      font-weight: 700;
      padding: 12px 14px;
      display: flex;
      justify-content: space-between;
    }
    .footer {
      margin-top: 16px;
      padding-top: 12px;
      border-top: 1px solid #e5e7eb;
      text-align: center;
      font-size: 11px;
      color: #6b7280;
      page-break-inside: avoid;
      break-inside: avoid;
      page-break-before: avoid;
      break-before: avoid;
    }
  </style>
</head>
<body>
  <div class="accent"></div>
  <table style="width:100%;border-collapse:collapse;margin-bottom:22px;">
    <tr>
      <td style="width:58%;vertical-align:top;padding:0;">
        ${
          mostrarInfoEmpresa
            ? `
          <div style="font-size:22px;font-weight:700;color:#0E1F33;letter-spacing:0.01em;margin:0 0 6px 0;">${escHtml(nombreEmpresa)}</div>
          ${direccionEmpresa ? `<div style="font-size:12px;color:#4b5563;margin:2px 0;">${escHtml(direccionEmpresa)}</div>` : ''}
          ${telefonoEmpresa ? `<div style="font-size:12px;color:#4b5563;margin:2px 0;">Tel: ${escHtml(telefonoEmpresa)}</div>` : ''}
          ${rncEmpresa ? `<div style="font-size:12px;color:#4b5563;margin:2px 0;">RNC: ${escHtml(rncEmpresa)}</div>` : ''}
          ${emailEmpresa ? `<div style="font-size:12px;color:#4b5563;margin:2px 0;">${escHtml(emailEmpresa)}</div>` : ''}
          <div style="font-size:12px;color:#4b5563;margin:8px 0 0 0;">Fecha: ${escHtml(formatoFechaLargo(fecha))}</div>
        `
            : `<div style="font-size:12px;color:#4b5563;">Fecha: ${escHtml(formatoFechaLargo(fecha))}</div>`
        }
      </td>
      <td style="width:42%;vertical-align:top;text-align:right;padding:0;">
        <div style="font-size:16px;font-weight:700;color:#0E1F33;letter-spacing:0.08em;margin-bottom:8px;text-transform:uppercase;">${escHtml(tituloDocumento)}</div>
        ${metaExtraHtml}
        ${fechaVencimiento ? `<div style="font-size:12px;color:#4b5563;margin:2px 0;">Fecha de vencimiento: ${escHtml(fechaVencimiento)}</div>` : ''}
        
      </td>
    </tr>
  </table>

  ${parteHtml}

  <div class="section-title">Detalle</div>
  <table class="items">
    <thead>
      <tr>
        <th style="width:12%;">Código</th>
        <th style="width:38%;">Descripción</th>
        <th style="width:10%;text-align:center;">Cant.</th>
        <th style="width:13%;text-align:right;">Precio</th>
        <th style="width:13%;text-align:right;">Desc.</th>
        <th style="width:14%;text-align:right;">Subtotal</th>
      </tr>
    </thead>
    <tbody>${itemsHtml}</tbody>
  </table>

  ${cierreHtml}

  ${
    observaciones
      ? `<div style="margin-top:16px;">
          <div class="section-title">Observaciones</div>
          <div style="font-size:12px;color:#4b5563;white-space:pre-wrap;">${escHtml(observaciones)}</div>
        </div>`
      : ''
  }

  <div class="footer">
    ${mensajeGracias ? `<div style="font-weight:600;color:#0E1F33;margin-bottom:6px;">${escHtml(mensajeGracias)}</div>` : ''}
    ${leyendaPiePagina ? `<div>${escHtml(leyendaPiePagina)}</div>` : ''}
    ${footerNote ? `<div style="margin-top:10px;">${escHtml(footerNote)}</div>` : ''}
  </div>
  ${qrDataUrl ? scriptQrCartaSinSalto : ''}
</body>
</html>`;
};
